<script setup lang="ts">
import { refDebounced } from '@vueuse/core'
import { computed, ref } from 'vue'

import { Loader } from '@/shared'
import { SelectOptionsType, SelectOutputType } from '@/types'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
  ButtonVariants,
} from '@/ui'
import { cn, filteringSearchValues } from '@/utils'

const props = defineProps<{
  selectOptions: SelectOptionsType
  modelValue?: string
  placeholder?: string
  classNames?: string
  disabled?: boolean
  isLoading?: boolean
  error?: boolean
  maxItems?: number
  asChild?: boolean
  showReset?: boolean
  triggerSize?: ButtonVariants['size']
  withoutSorting?: boolean
}>()

const emits = defineEmits<{
  (e: 'update:model-value', val: string): void
  (e: 'handle-change', val: string): void
}>()

const isOpen = ref(false)
const searchTerm = ref('')
const debouncedSearch = refDebounced(searchTerm, 300)

const parsedValue = computed(() => {
  const { options } = props.selectOptions
  const modelValue = props.modelValue

  if (!Array.isArray(options) || !options || options.length === 0 || typeof options[0] === 'string') {
    return { value: modelValue || '', description: '' }
  }
  const option = (options as Array<Record<string, string>>).find(o => String(o.id) === String(modelValue))

  if (!option) {
    return { value: '', description: '' }
  }

  return {
    value: option.name ?? '',
    description: option.description ?? '',
  }
})

const filteredOptions = computed(() => {
  const { maxItems } = props
  const { options } = props.selectOptions

  if (debouncedSearch.value.length < 2) {
    const result = (maxItems && options.length > maxItems ? options.slice(0, maxItems) : options) as SelectOutputType[]
    if (props.withoutSorting) {
      return result
    }

    return props.modelValue
      ? result.filter(o => props.modelValue !== (typeof o === 'string' ? o : String(o.id)))
      : result
  }

  return filteringSearchValues(options, debouncedSearch.value, maxItems)
})

const toggleSelect = (val: string) => {
  const newValue = props.modelValue === val ? '' : val
  emits('update:model-value', newValue)
  emits('handle-change', newValue)
  isOpen.value = false
}

const isSelected = (option: SelectOutputType) =>
  props.modelValue === (typeof option === 'string' ? option : String(option.id))

const onFilter = (options, search) =>
  options.length === 0 ? [] : filteringSearchValues(options as SelectOptionsType['options'], search)
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger :as-child="asChild" as="div">
      <slot v-if="$slots.trigger" name="trigger" />
      <Button
        v-else
        :size="triggerSize"
        variant="outline"
        :disabled="props.disabled"
        :class="
          cn(
            'hover:bg-background border-input relative flex h-auto w-full justify-between border px-3 py-2 text-start text-xs focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 md:text-sm [&>span]:truncate',
            classNames,
            { 'border-red-500 text-red-500': error }
          )
        "
      >
        <Loader :is-loading="isLoading" absolute loader-class-names="size-5" />
        <div v-if="parsedValue.value" :class="['flex flex-col gap-y-1', { 'border-red-500 text-red-500': error }]">
          <span>{{ parsedValue.value }}</span>
          <span v-if="parsedValue.description" class="text-muted-foreground max-w-xs text-xs">
            {{ parsedValue.description }}
          </span>
        </div>
        <span v-else :class="['text-muted-foreground', { 'border-red-500 text-red-500': error }]">
          {{ placeholder }}
        </span>
        <Button
          v-if="showReset && modelValue"
          variant="link"
          size="sm"
          class="absolute top-[72%] right-8 h-auto -translate-y-[72%] p-0 sm:top-1/2 sm:-translate-y-1/2"
          @click.prevent="toggleSelect(modelValue)"
        >
          <Icon name="XMarkIcon" class="size-4" />
        </Button>
        <Icon name="ChevronDownIcon" class="ml-2 size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="relative w-auto p-0" align="start">
      <Command v-model:search-term="searchTerm" :filter-function="onFilter" as-child>
        <CommandInput class="m-1" :placeholder="placeholder" />
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandList>
          <CommandGroup :heading="selectOptions.label">
            <CommandItem
              v-if="modelValue && !withoutSorting"
              key="selected"
              :value="modelValue"
              @select="toggleSelect(modelValue)"
            >
              <Icon name="CheckIcon" class="mr-2 h-4 w-4 opacity-100" />
              <div class="flex flex-col gap-y-1">
                <Label>{{ parsedValue.value }}</Label>
                <span v-if="parsedValue.description" class="text-muted-foreground max-w-xs text-xs">
                  {{ parsedValue.description }}
                </span>
              </div>
            </CommandItem>
            <CommandItem
              v-for="option in filteredOptions"
              :key="typeof option === 'string' ? option : option.id"
              :value="option"
              @select="toggleSelect(typeof option === 'string' ? option : String(option.id))"
            >
              <Icon name="CheckIcon" :class="['mr-2 h-4 w-4', isSelected(option) ? 'opacity-100' : 'opacity-0']" />
              <div class="flex flex-col gap-y-1">
                <Label class="cursor-pointer">{{ typeof option === 'string' ? option : option.name }}</Label>
                <span
                  v-if="typeof option !== 'string' && option.description"
                  class="text-muted-foreground max-w-xs text-xs"
                >
                  {{ option.description }}
                </span>
              </div>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
